import { runInAction } from 'mobx';
import { useTranslation } from 'react-i18next';

import type {
  GoogleAdsAdGroup,
  GoogleAdsCreative,
  GoogleAdsSmartCampaign,
  IGoogleAdsAdGroup,
} from '@feathr/blackbox';
import {
  CampaignClass,
  CreativeClass,
  EIntegrationTypes,
  GoogleAdsSearchCreative,
} from '@feathr/blackbox';
import { showError } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import { toAdGroupView, toKeywordsPayload, toSitelinksPayload } from './AdStep.adapters';
import { createBlankDescriptions, createBlankHeadlines, createSitelink } from './AdStep.factories';
import type {
  IAd,
  IAdGroup,
  IUseAdGroupsResult,
  IValidateAdStepResult,
  TAdGroupPatch,
} from './AdStep.types';
import type { IAdGroupValidationInput } from './AdStep.utils';
import { validateAdStep } from './AdStep.utils';

interface IUseAdGroupsProps {
  readonly campaign: GoogleAdsSmartCampaign;
  /** The campaign's creatives, already fetched by the wizard. Search ads are creatives. */
  readonly creatives: ListResponse<GoogleAdsCreative>;
}

/** How many blank sitelinks and ads a brand new ad group starts with. */
const NEW_AD_GROUP_ROWS = 2;

/**
 * The ad groups being edited on the Ads step, and the mutators that change them.
 *
 * The models are the source of truth: the `IAdGroup` views are rebuilt every render and
 * the mutators write through with `model.set`, so there is no second copy to keep in
 * sync. Edits stay in memory until {@link IAdGroupsController.saveAdGroup}; publishing
 * sweeps up anything still dirty through `SaveCampaignButton`.
 */
export default function useAdGroups({
  campaign,
  creatives,
}: Readonly<IUseAdGroupsProps>): IUseAdGroupsResult {
  const { Creatives, GoogleAdsAdGroups } = useStore();
  const account = useAccount();
  const { t } = useTranslation();

  /*
   * The endpoint resolves its `campaign` parameter as a Search campaign, so asking it
   * about a Smart campaign is a guaranteed 404. `_cls` is widened back to the enum
   * because `campaign` is typed as Smart, as the wizard itself does.
   */
  const isSearchCampaign =
    (campaign.get('_cls') as CampaignClass) === CampaignClass.GoogleAdsSearch;
  const response = isSearchCampaign ? GoogleAdsAdGroups.list({ campaign: campaign.id }) : undefined;

  const models: GoogleAdsAdGroup[] = response ? [...response.models] : [];

  /*
   * Archived ads are filtered out here rather than dropped from the collection: a
   * removed ad has to stick around long enough for its `is_archived` to be saved.
   */
  function getAdsByAdGroup(): Map<string, GoogleAdsSearchCreative[]> {
    const byAdGroup = new Map<string, GoogleAdsSearchCreative[]>();
    creatives.models.forEach((creative) => {
      if (!(creative instanceof GoogleAdsSearchCreative) || creative.get('is_archived')) {
        return;
      }
      const adGroupId = creative.get('ad_group');
      const ads = byAdGroup.get(adGroupId);
      if (ads) {
        ads.push(creative);
      } else {
        byAdGroup.set(adGroupId, [creative]);
      }
    });
    return byAdGroup;
  }

  const adsByAdGroup = getAdsByAdGroup();

  const adGroups: IAdGroup[] = models.map((model) =>
    toAdGroupView(model, adsByAdGroup.get(model.id) ?? []),
  );

  /**
   * Derived on call rather than captured, because the wizard hands this to `useSteps`,
   * whose reaction holds the closure from the render it was created in and fires the
   * moment the fetches land. A result computed during that first render would tell the
   * wizard for ever that the campaign has no ad groups.
   */
  function getValidation(): IValidateAdStepResult {
    const currentModels = response ? [...response.models] : [];
    const currentAds = getAdsByAdGroup();
    const inputs: IAdGroupValidationInput[] = currentModels.map((model) => ({
      view: toAdGroupView(model, currentAds.get(model.id) ?? []),
      model,
      ads: currentAds.get(model.id) ?? [],
    }));
    return validateAdStep(inputs, t);
  }

  const validation = getValidation();

  function getAds(index: number): GoogleAdsSearchCreative[] {
    return adsByAdGroup.get(models[index]?.id) ?? [];
  }

  /** Every ad belonging to an ad group, archived ones included. */
  function getAllAds(adGroupId: string): GoogleAdsSearchCreative[] {
    return creatives.models.filter(
      (creative): creative is GoogleAdsSearchCreative =>
        creative instanceof GoogleAdsSearchCreative && creative.get('ad_group') === adGroupId,
    );
  }

  function discardAd(ad: GoogleAdsSearchCreative): void {
    runInAction(() => {
      creatives.models.remove(ad);
    });
    Creatives.removeEphemeral(ad.id);
  }

  function createAd(adGroup: GoogleAdsAdGroup): void {
    const creative = Creatives.create({
      _cls: CreativeClass.GoogleAd,
      account: account.id,
      ad_group: adGroup.id,
      descriptions: createBlankDescriptions(),
      destination_url: '',
      headlines: createBlankHeadlines(),
      integrations: [EIntegrationTypes.GoogleAds],
      is_enabled: true,
      is_feathr_approved: true,
      parent: campaign.id,
    }) as GoogleAdsSearchCreative;

    // The views derive from the fetched list, so push it in to make the ad appear. This
    // also puts it in the wizard's `childModels`, which is what saves it on publish.
    runInAction(() => {
      creatives.models.push(creative);
    });
  }

  /**
   * Creates an ad group and returns the index it landed at, or null if it wasn't created.
   *
   * The index is the caller's cue to open the new group for editing, and it has to come from
   * here: the list only grows once the server has answered, so an index guessed beforehand
   * points past the end of a list that never grew.
   */
  async function addAdGroup(): Promise<number | null> {
    if (!response) {
      return null;
    }

    /*
     * Created server-side straight away: an ad carries the id of the ad group it belongs
     * to and the API validates it, so the group has to exist before its ads can.
     */
    const adGroup = GoogleAdsAdGroups.create({
      campaign: campaign.id,
      keywords: [],
      name: t('New ad group'),
      sitelinks: [],
    });

    try {
      /*
       * `refreshApiCache: false` and a manual push, rather than letting the collection
       * refetch: a refetch landing after the placeholder rows below were set would
       * overwrite them with the server's empty lists.
       */
      const created = await GoogleAdsAdGroups.add(adGroup, { refreshApiCache: false });
      if (created.isErrored) {
        throw created.error;
      }

      // The server assigns the id, so the locally-keyed model is now a stale duplicate.
      GoogleAdsAdGroups.removeEphemeral(adGroup.id);
      runInAction(() => {
        response.models.push(created);
      });
      const newIndex = response.models.length - 1;

      // Blank rows, so what policy requires is visible before anything is filled in.
      // They stay local until they hold real values — see `saveAdGroup`.
      created.set({
        sitelinks: toSitelinksPayload(
          Array.from({ length: NEW_AD_GROUP_ROWS }, () => createSitelink()),
        ),
      });
      for (let index = 0; index < NEW_AD_GROUP_ROWS; index++) {
        createAd(created);
      }

      return newIndex;
    } catch (error) {
      GoogleAdsAdGroups.removeEphemeral(adGroup.id);
      showError(error, t);
      return null;
    }
  }

  async function removeAdGroup(index: number): Promise<void> {
    const adGroup = models[index];
    if (!adGroup) {
      return;
    }

    try {
      // The API refuses to delete an ad group that still has live ads, so its ads go first.
      for (const ad of getAllAds(adGroup.id)) {
        if (ad.isEphemeral) {
          discardAd(ad);
          continue;
        }

        /*
         * `removeAd` archives in memory and waits for the group to be saved, so an
         * `is_archived` the model hasn't persisted yet says nothing about the server —
         * which still has that ad live, and refuses the delete because of it. Only an ad
         * the server itself reported as archived can be passed over.
         */
        if (ad.get('is_archived') && !ad.isAttributeDirty('is_archived')) {
          continue;
        }

        await ad.archive();
        if (ad.isErrored) {
          throw ad.error;
        }
      }

      await adGroup.delete();
      if (adGroup.isErrored) {
        throw adGroup.error;
      }
    } catch (error) {
      showError(error, t);
    }
  }

  function updateAdGroup(index: number, patch: TAdGroupPatch): void {
    const adGroup = models[index];
    const view = adGroups[index];
    if (!adGroup || !view) {
      return;
    }

    const attributes: Partial<IGoogleAdsAdGroup> = {};
    if (patch.name !== undefined) {
      attributes.name = patch.name;
    }
    // Both keyword editors write into one list, so either patch rebuilds the whole thing.
    if (patch.keywords || patch.negativeKeywords) {
      attributes.keywords = toKeywordsPayload(
        patch.keywords ?? view.keywords,
        patch.negativeKeywords ?? view.negativeKeywords,
      );
    }
    if (patch.sitelinks) {
      attributes.sitelinks = toSitelinksPayload(patch.sitelinks);
    }

    adGroup.set(attributes);
  }

  function renameAdGroup(index: number, name: string): void {
    updateAdGroup(index, { name });
  }

  function addAd(index: number): void {
    const adGroup = models[index];
    if (adGroup) {
      createAd(adGroup);
    }
  }

  function removeAd(index: number, adIndex: number): void {
    const ad = getAds(index)[adIndex];
    if (!ad) {
      return;
    }

    if (ad.isEphemeral) {
      discardAd(ad);
      return;
    }
    // Archived in memory, so removal lands with every other edit when the group is saved.
    ad.set({ is_archived: true });
  }

  function updateAd(index: number, adIndex: number, patch: Partial<Omit<IAd, 'key'>>): void {
    const ad = getAds(index)[adIndex];
    if (!ad) {
      return;
    }

    ad.set({
      ...(patch.headlines ? { headlines: patch.headlines } : {}),
      ...(patch.descriptions ? { descriptions: patch.descriptions } : {}),
      ...(patch.finalUrl !== undefined ? { destination_url: patch.finalUrl } : {}),
    });
  }

  /**
   * Persists one ad group and its ads.
   *
   * An unfinished ad is still sent, but a half-filled sitelink is not: the sitelink endpoint
   * needs both a label and a URL, while a creative needs only its ad group. Either way the
   * step's own validation is what reports the gap.
   */
  async function saveAdGroup(index: number): Promise<void> {
    const adGroup = models[index];
    if (!adGroup) {
      return;
    }

    try {
      // Routed through `save()`, which is the one place that prunes placeholder sitelinks
      // and sends both lists whole — the API replaces them rather than merging.
      if (adGroup.isDirty) {
        await adGroup.save();
        if (adGroup.isErrored) {
          throw adGroup.error;
        }
      }

      // Ads go after their group, so a rejected keyword surfaces before any ad is written.
      for (const ad of getAllAds(adGroup.id)) {
        if (ad.isEphemeral) {
          /*
           * Sent even when unfinished. The endpoint asks only for an `ad_group`, so an ad
           * with no copy yet still persists — skipping it instead meant the two blank ads a
           * new ad group starts with were silently dropped and gone after a reload.
           */
          const created = await Creatives.add(ad);
          if (created.isErrored) {
            throw created.error;
          }
        } else if (ad.isDirty) {
          await ad.save();
          if (ad.isErrored) {
            throw ad.error;
          }
        }
      }
    } catch (error) {
      showError(error, t);
    }
  }

  return {
    addAd,
    addAdGroup,
    adGroupModels: models,
    adGroups,
    validation,
    getValidation,
    // A failed fetch counts as loaded, or the step would show a loading placeholder
    // forever and the wizard would keep Next disabled with nothing to explain why.
    isLoaded: !isSearchCampaign || !!response?.hasLoadedOnce || !!response?.isErrored,
    // Read when called rather than closed over, so the wizard's `waitFor` reaction picks
    // the fetch completing up. See `IUseAdGroupsResult`.
    hasLoaded: (): boolean =>
      !isSearchCampaign || !!response?.hasLoadedOnce || !!response?.isErrored,
    loadError: response?.isErrored ? response.error ?? new Error('Unknown error') : null,
    removeAd,
    removeAdGroup,
    renameAdGroup,
    saveAdGroup,
    updateAd,
    updateAdGroup,
  };
}
